import React from 'react'

import { gql } from 'graphql-tag'

import { useMutation } from '@apollo/client/react'

import { useAuth } from 'src/auth'

const UPDATE_HEARTBEAT_MUTATION = gql`
  mutation UpdateApplicantHeartbeat($applicantId: Int!) {
    updateApplicantHeartbeat(applicantId: $applicantId)
  }
`

const HEARTBEAT_INTERVAL = 5 // 5 seconds (must be consistent with the heartbeat interval in the backend)
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
]

export const useActivityHeartbeat = (
  applicantId: number | undefined,
  shouldRunHeartbeat: boolean
) => {
  const [updateHeartbeat] = useMutation(UPDATE_HEARTBEAT_MUTATION)
  const hasActivityRef = React.useRef(true) // Start with true to send initial heartbeat

  React.useEffect(() => {
    if (!applicantId || !shouldRunHeartbeat) return

    // Send heartbeat function
    const sendHeartbeat = async () => {
      try {
        await updateHeartbeat({
          variables: {
            applicantId,
          },
        })
      } catch (error) {
        console.error('Heartbeat failed:', error)
      }
    }

    // Mark activity function
    const markActivity = () => {
      hasActivityRef.current = true
    }

    // Send initial heartbeat
    void sendHeartbeat()

    // Add activity listeners
    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, markActivity, { passive: true })
    })

    // Track page visibility and delete heartbeat on hide
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        hasActivityRef.current = true
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Start heartbeat interval
    const intervalId = setInterval(() => {
      if (hasActivityRef.current) {
        void sendHeartbeat()
        hasActivityRef.current = false // Reset after sending
      }
    }, HEARTBEAT_INTERVAL * 1000)

    // TODO: if deemed appropriate, add window blur/focus listeners
    // We could make make assumptions that are accurate a higher percentage of the time by using
    // inactiveAt in addition to lastActiveTime, keeping in mind that there exists no reliable way to
    // record lastActiveTime in all cases.
    // Deleting the heartbeat on page hide is not acceptable with the current implementation of SMS
    // suppression which is why inactiveAt is proposed. (ask Weston if it's unclear)
    // If changing this code, one should also consider the "hands in lap" problem, which is the fact
    // that we cannot determine if a perp is sitting at their computer reading the page with their
    // hands in their lap, or in the next room, not reading the page.

    // Cleanup
    return () => {
      if (!shouldRunHeartbeat) return
      clearInterval(intervalId)
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, markActivity)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [applicantId, updateHeartbeat, shouldRunHeartbeat])
}

export const tryEnablingHeartbeat = (applicantId: number | undefined) => {
  const { currentUser } = useAuth()
  const roles = currentUser?.roles as string[]
  const isApplicant = roles?.includes('Applicant')
  const isSuper = roles?.includes('Super')
  const shouldRunHeartbeat =
    (isApplicant && !isSuper) ||
    (process.env.TWODOTS_ENV === 'local' && isSuper)
  useActivityHeartbeat(applicantId, shouldRunHeartbeat)
}
